import * as React from 'react';
import { Link } from '../Link';
import { BuilderName } from '../BuilderName';
import { IBuilder } from '../../types/newbuilding';
import * as styles from './MultipleBuilders.css';
import { ActionLink, IconFunctionalInformationCircle16, InformationTooltip, UIHeading4 } from '@cian/ui-kit';
import { IconActionChevronRightSmall16 } from '@cian/ui-kit-design-tokens/icons';
import { BuildersTooltip } from '../BuildersTooltip';

export interface IMultipleBuildersProps {
  builders: IBuilder[];
  onBuilderClick?: () => void;
  onInfoClick: () => void;
  isPromoSnippet?: boolean;
}

const BUILDERS_TO_SHOW = 2;

export function MultipleBuilders({ builders, onBuilderClick, onInfoClick }: IMultipleBuildersProps) {
  const buildersToRender = React.useMemo(() => {
    if (builders.length > BUILDERS_TO_SHOW) {
      return builders.slice(0, BUILDERS_TO_SHOW);
    }

    return builders;
  }, [builders]);

  const [isOpen, setIsOpen] = React.useState(false);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const buildersRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const checkTruncation = () => {
      const element = buildersRef.current;
      if (element) {
        setIsTruncated(element.scrollHeight > element.clientHeight);
      }
    };

    checkTruncation();

    window.addEventListener('resize', checkTruncation);

    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, []);

  const showMore = isTruncated || builders.length > BUILDERS_TO_SHOW;
  const moreButtonText = React.useMemo(() => {
    let count = builders.length - BUILDERS_TO_SHOW;
    if (isTruncated) {
      count += 1;
    }

    return `Ещё ${count}`;
  }, [builders.length, isTruncated]);

  const onClick = React.useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = React.useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div className={styles['container']}>
      <div className={styles['title']}>
        <UIHeading4 color="text-secondary-default">Застройщики</UIHeading4>
        <div onClick={onInfoClick} className={styles['icon']}>
          <IconFunctionalInformationCircle16 color="icon-secondary-default" />
        </div>
      </div>
      <div className={styles['builders']} ref={buildersRef}>
        {buildersToRender.map((builder, index) => {
          const notLast = index + 1 < buildersToRender.length;

          return (
            <Link
              key={`${builder}${index}`}
              url={buildersToRender[index].url}
              target="blank"
              onClick={onBuilderClick}
              linkStyle={styles['builder-link']}
            >
              <BuilderName name={`${builder.name}${notLast ? ',' : ''}${notLast ? ' ' : ''}`} />
            </Link>
          );
        })}
      </div>
      {showMore && (
        <div className={styles['button']} data-testid="multiple-builders-info-button">
          <InformationTooltip
            open={isOpen}
            onClose={onClose}
            content={<BuildersTooltip builders={builders} onBuilderClick={onBuilderClick} />}
            placement="top-end"
          >
            <ActionLink
              color="current_color"
              onClick={onClick}
              fontSize="M"
              fontWeight="bold"
              afterIcon={<IconActionChevronRightSmall16 color="current_color" />}
            >
              {moreButtonText}
            </ActionLink>
          </InformationTooltip>
        </div>
      )}
    </div>
  );
}
